import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, BellRing, MailIcon, PhoneIcon, Calendar, Check, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@radix-ui/themes';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '../ui/toaster';
import { useGetNotificationsChannelsQuery, useGetUsersMeQuery, usePostNotificationsChannelsMutation } from '@/services';

type Channel = {
  name: string;
  type: 'email' | 'sms' | 'push';
  status: string;
  config: {
    critical: boolean;
    high: boolean;
    medium: boolean;
    low: boolean;
    info: boolean;
  };
  tenant_pk: number;
  user_pk?: number | null;
  device_pk?: number | null;
  id: string | null;
  pk: number | null;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

interface NotificationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId?: string | null;
  settingType: 'general' | 'tenant' | 'device';
}

const channelDescription = {
  email: 'Receive alerts via email',
  sms: 'Receive alerts via text message',
  push: 'Receive alerts on mobile app',
};

const severityInfo = [
  {
    key: 'critical',
    label: 'Critical',
    color: 'red',
    description: 'Critical issues that need immediate attention',
  },
  {
    key: 'high',
    label: 'High',
    color: 'orange',
    description: 'Important issues that need attention soon',
  },
  {
    key: 'medium',
    label: 'Medium',
    color: 'yellow',
    description: 'Issues that may need attention',
  },
  {
    key: 'low',
    label: 'Low',
    color: 'blue',
    description: 'Minor issues worth knowing about',
  },
  {
    key: 'info',
    label: 'Info',
    color: 'green',
    description: 'Informational messages',
  },
];

function getChannelIcon(channelType: string) {
  if (channelType === 'email') {
    return (
      <div className="bg-blue-100 dark:bg-blue-900/30 p-2.5 rounded-full">
        <MailIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      </div>
    );
  }
  if (channelType === 'sms') {
    return (
      <div className="bg-green-100 dark:bg-green-900/30 p-2.5 rounded-full">
        <PhoneIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
      </div>
    );
  }
  if (channelType === 'push') {
    return (
      <div className="bg-purple-100 dark:bg-purple-900/30 p-2.5 rounded-full">
        <BellRing className="h-5 w-5 text-purple-600 dark:text-purple-400" />
      </div>
    );
  }
  return null;
}

function SeveritySettings({
  config,
  type,
  setForm,
}: {
  config: Record<string, boolean>;
  type: string;
  setForm: React.Dispatch<React.SetStateAction<any>>;
}) {
  return (
    <div className="w-full max-w-4xl mx-auto grid grid-cols-3 gap-4">
      {severityInfo.map(({ key, label, color, description }) => (
        <div key={key} className="border rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{label}</span>
            <Badge radius="full" color={color} size="1">
              {label}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">{description}</p>
          <div className="flex items-center justify-between pt-1">
            <Switch
              id={`${key}-severity`}
              defaultChecked={config[key]}
              onCheckedChange={(checked) => {
                setForm((prev) => ({
                  ...prev,
                  channels: prev.channels.map((channel) => {
                    if (channel.type === type) {
                      return {
                        ...channel,
                        config: {
                          ...channel.config,
                          [key]: checked,
                        },
                      };
                    }
                    return channel;
                  }),
                }));
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function ChannelItem({
  channel,
  setForm,
  form,
}: {
  channel: Channel;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  form: any;
}) {
  const [expanded, setExpanded] = useState(false);
  const channelType = channel.type as keyof typeof channelDescription;
  const currentChannel = form.channels.find((c) => c.type === channel.type);

  return (
    <div key={channel.pk} className="border rounded-lg p-4 mb-2">
      <div className="flex items-center gap-4">
        {getChannelIcon(channelType)}
        <div className="flex-1">
          <h3 className="font-medium">{channel.name}</h3>
          <p className="text-sm text-muted-foreground">{channelDescription[channelType]}</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            defaultChecked={channel.status === 'active'}
            onCheckedChange={(checked) => {
              setForm((prev) => ({
                ...prev,
                channels: prev.channels.map((c) => {
                  if (channel.type === c.type) {
                    return {
                      ...c,
                      status: checked ? 'active' : 'paused',
                    };
                  }
                  return c;
                }),
              }));
            }}
          />
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          disabled={currentChannel?.status === 'paused'}
          aria-expanded={expanded}
          aria-controls={`expand-${channel.pk}`}
        >
          <ChevronDown className={`transform transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <div
        className={`overflow-hidden transition-max-height duration-500 ease-in-out`}
        style={{
          maxHeight: expanded ? '300px' : '0px',
        }}
      >
        {expanded && (
          <div className="mt-4">
            <SeveritySettings setForm={setForm} config={channel.config} type={channel.type} />
          </div>
        )}
      </div>
    </div>
  );
}

export function NotificationsSettings({ isOpen, onClose, deviceId, settingType }: NotificationSettingsProps) {
  const [selectedTab, setSelectedTab] = useState('general');
  const titleName =
    settingType == 'device'
      ? 'Device notifications'
      : settingType == 'tenant'
        ? 'Tenant Notifications'
        : 'General Notifications';
  const { data: currentUserData } = useGetUsersMeQuery();

  const [postUserChannel] = usePostNotificationsChannelsMutation();
  const [postTenantChannel] = usePostNotificationsChannelsMutation();
  const [saving, setSaving] = useState(false);

  const queryParams: Record<string, string | null> = {};
  if (settingType === 'device' && deviceId) {
    queryParams.deviceId = deviceId;
  }
  if (settingType !== 'general' && currentUserData?.data.id) {
    queryParams.userId = currentUserData.data.id;
  }

  const { data: channelsData, refetch: refetchChannels } = useGetNotificationsChannelsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const [form, setForm] = useState({
    channels: [],
  });

  useEffect(() => {
    if (channelsData?.data) {
      const baseChannels = channelsData.data.map((channel) => ({
        name: channel.name,
        type: channel.type,
        status: channel.status,
        config: channel.config,
      }));

      const updatedForm = { channels: baseChannels };

      if (deviceId && settingType === 'device') {
        updatedForm.channels = baseChannels.map((channel) => ({
          ...channel,
          device_pk: deviceId,
        }));
      }

      setForm(updatedForm);
    }
  }, [channelsData, deviceId, settingType]);

  const handleSave = async () => {
    setSaving(true);

    let response;

    try {
      if (settingType === 'device' && deviceId) {
        response = await postUserChannel({
          body: {
            device_id: deviceId,
            channels: form.channels,
          },
        });
      } else if (settingType === 'tenant') {
        response = await postUserChannel({
          body: {
            channels: form.channels,
          },
        });
      } else if (settingType === 'general') {
        response = await postTenantChannel({
          body: {
            channels: form.channels,
          },
        });
      }

      if (!response || !response.data?.success) {
        toast.error('Failed to save configuration.');
        return;
      }

      await refetchChannels();
      onClose();
      toast.success('Configuration saved successfully.');
    } catch (error) {
      toast.error('Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[650px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-6 shadow-lg focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] overflow-auto">
          <div className="flex justify-between items-center mb-5">
            <Dialog.Title className="text-xl font-bold">{titleName}</Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <Tabs defaultValue={selectedTab} onValueChange={setSelectedTab} className="w-full">
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>

            <ScrollArea style={{ height: '450px' }} className="pr-3">
              <TabsContent value="general" className="space-y-6 max-h-[400px]">
                <div className="w-full h-full space-y-6">
                  {channelsData?.data?.map((channel) => (
                    <ChannelItem channel={channel} setForm={setForm} form={form} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="advanced" className="space-y-6 max-h-[400px]">
                <div className="space-y-5">
                  <div className="flex items-center gap-4 border rounded-lg p-4">
                    <div className="bg-amber-100 dark:bg-amber-900/30 p-2.5 rounded-full">
                      <Check className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Auto-acknowledge Alerts</h3>
                      <p className="text-sm text-muted-foreground">Automatically acknowledge low severity alerts</p>
                    </div>
                    <Switch id="auto-acknowledge" />
                  </div>

                  <div className="flex items-center gap-4 border rounded-lg p-4">
                    <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2.5 rounded-full">
                      <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Alert Retention Period</h3>
                      <p className="text-sm text-muted-foreground">How long to keep alert history</p>
                    </div>
                    <Select defaultValue="30">
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="365">1 year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="font-medium">Advanced Settings</h3>

                  <div className="space-y-3">
                    <Label htmlFor="alert-cooldown">Alert Cooldown Period (minutes)</Label>
                    <div className="grid grid-cols-5 gap-4 items-center">
                      <div className="col-span-4">
                        <Slider id="alert-cooldown" defaultValue={[15]} min={0} max={60} step={1} />
                      </div>
                      <div className="col-span-1">
                        <Input type="number" value="15" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Minimum time between duplicate alerts to prevent alert fatigue
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="batch-alerts">Alert Batching</Label>
                    <Select defaultValue="immediate">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Send immediately</SelectItem>
                        <SelectItem value="5min">Batch every 5 minutes</SelectItem>
                        <SelectItem value="15min">Batch every 15 minutes</SelectItem>
                        <SelectItem value="hourly">Batch hourly</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      How to group multiple alerts into batched notifications
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <Label htmlFor="quiet-hours" className="mb-1 block">
                        Quiet Hours
                      </Label>
                      <p className="text-xs text-muted-foreground">Only send critical alerts during overnight hours</p>
                    </div>
                    <Switch id="quiet-hours" />
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <div className="flex justify-end gap-3 mt-8">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
